export const DEFAULT_SENDER_EMAIL = 'contato@yourviews.com.br'

const YOURVIEWS_DOMAIN = 'yourviews.com.br'

// Mesma regra de StringExtensions.IsValidEmail no backend
const EMAIL_PATTERN =
  /^[a-zA-Z0-9.!#$%&'*+/=?^_`{|}~-]+@[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?(?:\.[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?)*$/i

// Mensagens devolvidas pelo backend (EnglishMessages.cs) para o remetente
const API_SENDER_EMAIL_ERRORS = {
  'Sender email domain is not allowed for this store':
    'campaign.fields.email.senderEmailDomainNotAllowed',
  'The email provider refused this sender. Check that the sender domain is authenticated with the email provider':
    'campaign.fields.email.senderEmailRefused'
}

const normalize = (value) => (value ?? '').trim().toLowerCase()

export const isValidSenderEmailFormat = (email) =>
  EMAIL_PATTERN.test((email ?? '').trim())

export const isSenderEmailAllowed = (email, allowedDomains) => {
  const normalizedEmail = normalize(email)

  if (normalizedEmail === DEFAULT_SENDER_EMAIL) return true

  const host = normalizedEmail.substring(normalizedEmail.lastIndexOf('@') + 1)
  if (!host || host === YOURVIEWS_DOMAIN) return false

  return (allowedDomains ?? []).some((domain) => normalize(domain) === host)
}

/**
 * Returns the i18n key of the sender email error, or null when it is valid.
 * The domain is only checked when the store settings were loaded.
 */
export const validateSenderEmail = (email, settings) => {
  if (!(email ?? '').trim()) return 'campaign.fields.email.senderEmailRequired'

  if (!isValidSenderEmailFormat(email))
    return 'campaign.fields.email.senderEmailInvalid'

  if (
    settings?.AllowedDomains &&
    !isSenderEmailAllowed(email, settings.AllowedDomains)
  )
    return 'campaign.fields.email.senderEmailDomainNotAllowed'

  return null
}

/**
 * Returns the store defaults for the empty sender fields, or null when there
 * is nothing to fill. A saved value is never replaced.
 */
export const getSenderDefaults = (emailConfiguration, settings) => {
  if (!settings) return null

  const defaults = {}

  if (!emailConfiguration?.SenderEmail && settings.DefaultSenderEmail)
    defaults.SenderEmail = settings.DefaultSenderEmail

  if (!emailConfiguration?.SenderName && settings.DefaultSenderName)
    defaults.SenderName = settings.DefaultSenderName

  return Object.keys(defaults).length > 0 ? defaults : null
}

/**
 * Translates a sender email error returned by the API, or returns null when
 * the message is not a sender email error.
 */
export const translateSenderEmailApiError = (message, t) => {
  const key = API_SENDER_EMAIL_ERRORS[message]

  return key ? t(key) : null
}

/**
 * Splits a sender email into the mailbox (before the last @) and the domain.
 */
export const splitSenderEmail = (email) => {
  const value = (email ?? '').trim()
  const at = value.lastIndexOf('@')

  if (at < 0) return { mailbox: value, domain: '' }

  return {
    mailbox: value.substring(0, at),
    domain: value.substring(at + 1).toLowerCase()
  }
}

/**
 * Domain options of the sender field: the store allowed domains plus the
 * Yourviews sender. A saved domain outside the list stays visible as not
 * allowed, so the admin sees why the campaign cannot be saved.
 */
export const getSenderDomainOptions = (allowedDomains, currentDomain) => {
  const domains = (allowedDomains ?? []).map((domain) => normalize(domain))
  const options = domains.map((domain) => ({ value: domain, allowed: true }))

  if (
    currentDomain &&
    currentDomain !== YOURVIEWS_DOMAIN &&
    !domains.includes(currentDomain)
  )
    options.push({ value: currentDomain, allowed: false })

  options.push({ value: YOURVIEWS_DOMAIN, allowed: true, yourviews: true })

  return options
}

/**
 * Builds the sender email from the mailbox and the domain. In the Yourviews
 * domain only DEFAULT_SENDER_EMAIL is allowed, so the mailbox is ignored.
 */
export const buildSenderEmail = (mailbox, domain) => {
  if (domain === YOURVIEWS_DOMAIN) return DEFAULT_SENDER_EMAIL

  const trimmedMailbox = (mailbox ?? '').trim()

  if (!trimmedMailbox) return ''

  return `${trimmedMailbox}@${domain ?? ''}`
}
